/**
 * Thin wrapper over the public campaign endpoints, following the landing-demo
 * pattern: the centralised api-client does the transport, this module owns the
 * error shape the UI branches on.
 *
 * Lookups go through the PUBLIC client (no bearer, no X-Active-Context, even
 * for a signed-in visitor). The claim goes through the AUTHED-no-context
 * client: the grant always lands on the claimer's own organisation, so the
 * acting workspace is irrelevant — and a stale stored context would 403 the
 * call for no reason.
 */

import { reportHandledException } from '@/lib/error-reporting'
import {
  ApiError,
  claimCampaign,
  getCampaignBySlug,
  getFeaturedCampaign,
  type ClaimedCampaign,
  type PublicCampaign,
} from '@kiao-voice/api-client'
import {
  EMAIL_VERIFICATION_REQUIRED_ERROR,
  type EmailVerificationRequiredErrorCode,
} from '@kiao-voice/core'
import { getOrCreateDeviceId } from '../../lib/identity'
import {
  createWebPublicApiClient,
  getWebPublicApiClient,
  createWebAuthedNoContextClient,
  getWebAuthedNoContextClient,
} from '../../lib/web-api-client'

export type { PublicCampaign, ClaimedCampaign }

/** Reasons a claim can fail, mapped from the API's error codes. */
export type CampaignClaimErrorCode =
  | 'promotion_claim_limit_reached'
  | 'promotion_already_claimed'
  | 'live_subscription_exists'
  | 'promotion_unavailable'
  | 'account_deactivated'
  /** The claim would consume an invite emailed to this address, and the account never proved it. */
  | EmailVerificationRequiredErrorCode
  | 'rate_limited'
  | 'unknown'

export class CampaignApiError extends Error {
  readonly status: number
  readonly code: CampaignClaimErrorCode
  /** Seconds to wait, when the API supplied one on a 429. */
  readonly retryAfterSecs: number | null

  constructor(status: number, body: unknown) {
    const raw =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: unknown }).error
        : null
    const code = typeof raw === 'string' ? raw : 'unknown'
    super(`Campaign API error ${status}: ${code}`)
    this.name = 'CampaignApiError'
    this.status = status
    this.code = KNOWN_CODES.has(code) ? (code as CampaignClaimErrorCode) : 'unknown'
    const retry =
      body && typeof body === 'object' && 'retryAfterSecs' in body
        ? (body as { retryAfterSecs: unknown }).retryAfterSecs
        : null
    this.retryAfterSecs = typeof retry === 'number' && retry > 0 ? retry : null
  }
}

const KNOWN_CODES = new Set<string>([
  'promotion_claim_limit_reached',
  'promotion_already_claimed',
  'live_subscription_exists',
  'promotion_unavailable',
  'account_deactivated',
  EMAIL_VERIFICATION_REQUIRED_ERROR,
  'rate_limited',
])

function toCampaignError(error: unknown): unknown {
  return error instanceof ApiError ? new CampaignApiError(error.status, error.body) : error
}

interface RequestOptions {
  /** Test seam — inject a mock fetch. */
  fetchFn?: typeof fetch
  signal?: AbortSignal
}

function publicClient(opts: RequestOptions) {
  return opts.fetchFn ? createWebPublicApiClient(opts.fetchFn) : getWebPublicApiClient()
}

function authedClient(opts: RequestOptions) {
  return opts.fetchFn ? createWebAuthedNoContextClient(opts.fetchFn) : getWebAuthedNoContextClient()
}

/** The campaign the marketing site should show, or null when none is listed. */
export async function fetchFeaturedCampaign(
  opts: RequestOptions = {}
): Promise<PublicCampaign | null> {
  try {
    const body = await getFeaturedCampaign(publicClient(opts), { signal: opts.signal })
    return body.campaign
  } catch (error) {
    reportHandledException(error, { source: 'features/promotion-campaign/api-client.ts' })
    const mapped = toCampaignError(error)
    // 404 is the ordinary "no campaign running" answer, not a failure — the
    // section simply does not render.
    if (mapped instanceof CampaignApiError && mapped.status === 404) return null
    throw mapped
  }
}

/** One campaign by slug, or null when it is not publicly reachable. */
export async function fetchCampaignBySlug(
  slug: string,
  opts: RequestOptions = {}
): Promise<PublicCampaign | null> {
  try {
    const body = await getCampaignBySlug(publicClient(opts), slug, { signal: opts.signal })
    return body.campaign
  } catch (error) {
    reportHandledException(error, { source: 'features/promotion-campaign/api-client.ts' })
    const mapped = toCampaignError(error)
    if (mapped instanceof CampaignApiError && mapped.status === 404) return null
    throw mapped
  }
}

/**
 * Claim the campaign as the signed-in user. Throws {@link CampaignApiError}.
 *
 * Carries this browser's anonymous id so the grant can be attributed to the
 * anonymous session that produced it. Omitted when there is none to send
 * (SSR, cookies blocked); the server treats its absence as ordinary.
 */
export async function claimPublicCampaign(
  slug: string,
  opts: RequestOptions = {}
): Promise<ClaimedCampaign> {
  try {
    return await claimCampaign(authedClient(opts), slug, {
      signal: opts.signal,
      deviceId: getOrCreateDeviceId() || undefined,
    })
  } catch (error) {
    throw toCampaignError(error)
  }
}
